import { MemberStatus } from '~/constants';
import { WinNativeSetFocusContents } from '~/constants/nativeApi';
import GA from '~/services/GA/GA';
import electronService from '~/services/nativeService/electronService';
import winNativeService from '~/services/nativeService/winNativeService';
import Actions from '~/stores/Actions';
import logging from '~/stores/providers/logging';
import { userStore } from '~/stores/UserStore';
import rename from '~/utils/fnUtil/rename';
import { getSubDomain, isElectron, isNative, isWinNative } from '~/utils/runEnvironmentUtil';
import { isString } from 'lodash';
import { mergeRight, omit } from 'ramda';
import curry from 'ramda/es/curry';

import { stores } from '../Stores';

class NativeActions {
  constructor(private actions: Actions) {
    this.clearCache();
  }

  private clearCache = () => {
    if (isElectron()) {
      try {
        electronService.clearCache();
      } catch (error) {
        logging.log('clear Cache Error');
        GA.writeClearCacheError();
      }
      return;
    }
  };

  private flipArgs = (func) => {
    return (e, data) => {
      func(data, e);
    };
  };

  private parseArgs = (func) => (data) => {
    func(isString(data) ? JSON.parse(data) : data);
  };

  on = (type, callback) => {
    if (isElectron()) {
      electronService.on(type, this.flipArgs(callback));
    } else if (isWinNative()) {
      if (type === 'bothView:screenCapture') {
        winNativeService.on(type, callback);
      } else {
        winNativeService.on(type, this.parseArgs(callback));
      }
    }
  };

  onSuspend = (callback) => {
    this.on('mainView:suspend', callback);
  };

  onResume = (callback) => {
    this.on('mainView:resume', callback);
  };

  onGetCount = (callback) => {
    this.on('mainView:getCount', callback);
  };

  onGetStatus = (callback) => {
    this.on('mainView:getStatus', callback);
  };

  sendEndFreeTrial() {
    this.send('END_FREE_TRIAL', {});
  }

  send = (type, contents) => {
    if (isElectron()) {
      electronService.send(type, JSON.parse(JSON.stringify(this.assignTenantDomain(contents))));
    } else if (isWinNative()) {
      winNativeService.send({
        type,
        contents: this.assignTenantDomain(contents),
      });
    }
  };

  sendLogin = (contents) => {
    this.send('application:login', contents);
  };

  sendUserLocaleToElectron = () => {
    const { id } = userStore;
    const user = stores.user;
    this.sendLocaleChanged({ memberId: id, locale: user.locale });
  };

  sendLocaleChanged = ({ memberId, locale }) => {
    if (isElectron()) {
      this.send('websocket:onMessage', {
        data: {
          type: 'member',
          action: 'update',
          content: { memberId, locale },
        },
      });
    } else if (isWinNative()) {
      this.send('application:localeChanged', { locale });
    }
  };

  sendUserInfoToElectron = (channelId) => {
    const { id, orgId } = userStore;
    this.send('application:memberme', {
      id,
      lastChannelId: channelId,
      orgId,
      projectInquireLink: this.actions.memberActions.getInquireUrl(),
    });
  };

  sendStatusChange = ({ memberId, memberStatus }) => {
    if (isElectron()) {
      this.send('websocket:onMessage', {
        data: {
          type: 'memberStatus',
          action: 'update',
          content: { memberId, memberStatus },
        },
      });
    } else if (isWinNative()) {
      this.send('application:statusChange', { memberStatus });
    }
  };

  private assignTenantDomain = (data) => {
    return mergeRight(data, {
      tenant: getSubDomain(),
      domainInfo: rename({ tenantId: 'id' }, userStore.tenant),
    });
  };

  private handleScreenCaptureForWinNative = curry((callback, arrayBuffer) => {
    const tempArray = new Uint8Array(arrayBuffer);
    const arrayBufferLength = tempArray.length;
    const uInt8Array = new Uint8Array(arrayBufferLength);
    for (let i = 0; i < arrayBufferLength; i += 1) {
      uInt8Array[i] = tempArray[i];
    }
    callback({ buffer: uInt8Array });
  });

  off = (type, callback) => {
    if (isElectron()) {
      electronService.off(type, callback);
    } else if (isWinNative()) {
      winNativeService.off(type, callback);
    }
  };

  offStatusChange = (callback) => {
    const type = 'bothView:screenCapture';
    if (isElectron()) {
      this.off(type, callback);
    } else if (isWinNative()) {
      this.off(type, this.handleScreenCaptureForWinNative(callback));
    }
  };

  offShowChannel = (callback) => {
    this.off('mainView:showChannel', callback);
  };

  onShowChannel = (callback) => {
    this.on('mainView:showChannel', callback);
  };

  offClickNotification = (callback) => {
    this.off('mainView:onClickNotification', callback);
  };

  onScreenCapture = (callback) => {
    const type = 'bothView:screenCapture';
    if (isElectron()) {
      this.on(type, callback);
    } else if (isWinNative()) {
      this.on(type, this.handleScreenCaptureForWinNative(callback));
    }
  };

  onStatusChange = (callback: (data: { memberStatus: MemberStatus | 'LOGOUT' }) => void) => {
    this.on('mainView:statusChange', callback);
  };

  sendDockBadge = (contents: { count: number }) => {
    if (isNative()) {
      this.send('application:badgeCountChanged', contents);
      return;
    } else {
      this.setAppBadge(contents.count);
    }
  };

  sendChannelChanged = (contents) => {
    this.send('application:channelChanged', contents);
  };

  sendDefaultDomain = (contents) => {
    this.send('application:defaultDomain', contents);
  };

  sendScreenCapture = (contents) => {
    this.send('application:screenCapture', contents);
  };

  sendNotification = (contents) => {
    this.send('application:notification', isElectron() ? omit(['onClick'], contents) : contents);
  };

  sendLogout(contents) {
    this.send('application:logout', contents);
  }

  sendSetFocus(contents: WinNativeSetFocusContents) {
    // windows Native용 방어코드
    this.send('application:setFocus', contents);
  }

  sendUpdateStatus(status) {
    this.send('application:updateStatus', status);
  }

  hasFocus() {
    return isElectron() ? electronService.hasFocus() : document.hasFocus();
  }

  private setAppBadge = (count: number) => {
    Number.isInteger(count) && navigator.setAppBadge?.(count);
  };
}

export default NativeActions;
